import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Logo from '../Logo';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: 'Développement Web', href: '/services/developpement-web' },
    { name: 'SEO & Référencement', href: '/services/seo-referencement' },
    { name: 'Applications Mobiles', href: '/services/applications-mobiles' },
    { name: 'Cybersécurité', href: '/services/cybersecurite' },
    { name: 'Automatisation & IA', href: '/services/automatisation-ia' },
    { name: 'Maintenance', href: '/services/maintenance-securite' }
  ];

  const zones = [
    { name: 'Lille', href: '/zone-intervention/lille' },
    { name: 'Roncq', href: '/zone-intervention/roncq' },
    { name: 'Phalempin', href: '/zone-intervention/phalempin' },
    { name: 'Seclin', href: '/zone-intervention/seclin' },
    { name: 'Tourcoing', href: '/zone-intervention/tourcoing' },
    { name: 'Roubaix', href: '/zone-intervention/roubaix' }
  ];

  const legal = [
    { name: 'Mentions légales', href: '/mentions-legales' },
    { name: 'Politique de confidentialité', href: '/confidentialite' },
    { name: 'CGV', href: '/cgv' }
  ];

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay: 0.2 }}
      className="py-16 px-4 sm:px-6 lg:px-8 border-t border-[var(--border-glass)] bg-[#020010] relative z-10"
    >
      <div className="max-w-7xl mx-auto">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <Logo width="150" height="40" className="object-contain opacity-80 hover:opacity-100 transition-opacity" idSuffix="Footer" />
            </Link>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              Agence web et dépannage informatique à Roncq et Phalempin.
              Solutions digitales sur mesure pour les professionnels du Nord.
            </p>
            <div className="space-y-3">
              <a href="tel:+33689177385" className="flex items-center gap-3 text-gray-400 hover:text-[var(--secondary)] transition-colors text-sm">
                <span className="text-lg">📞</span>
                <span>06 89 17 73 85</span>
              </a>
              <a href="mailto:contact@olney.dev" className="flex items-center gap-3 text-gray-400 hover:text-[var(--secondary)] transition-colors text-sm">
                <span className="text-lg">✉️</span>
                <span>contact@olney.dev</span>
              </a>
              <p className="flex items-center gap-3 text-gray-400 text-sm">
                <span className="text-lg">📍</span>
                <span>Roncq, Hauts-de-France</span>
              </p>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold mb-6 uppercase text-sm tracking-wider">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.href}>
                  <Link
                    to={service.href}
                    className="text-gray-400 hover:text-[var(--primary)] transition-colors text-sm"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Zones d'intervention */}
          <div>
            <h4 className="text-white font-bold mb-6 uppercase text-sm tracking-wider">Zones d'intervention</h4>
            <ul className="space-y-3">
              {zones.map((zone) => (
                <li key={zone.href}>
                  <Link
                    to={zone.href}
                    className="text-gray-400 hover:text-[var(--primary)] transition-colors text-sm"
                  >
                    {zone.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter & Social */}
          <div>
            <h4 className="text-white font-bold mb-6 uppercase text-sm tracking-wider">Restons en contact</h4>
            <p className="text-gray-400 text-sm mb-4">
              Recevez nos conseils tech et offres exclusives.
            </p>
            <form className="mb-6" onSubmit={(e) => e.preventDefault()}>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Votre email"
                  className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[var(--primary)] focus:outline-none transition-colors"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg text-sm font-semibold hover:bg-[var(--primary)]/80 transition-colors"
                >
                  OK
                </button>
              </div>
            </form>

            {/* Social Links */}
            <div className="flex gap-4">
              <a
                href="https://linkedin.com/company/olney-tech"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-[var(--primary)] hover:text-white transition-all"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a
                href="https://github.com/olney-tech"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-[var(--primary)] hover:text-white transition-all"
                aria-label="GitHub"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
              <a
                href="https://wa.me/33689177385"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-[#25D366] hover:text-white transition-all"
                aria-label="WhatsApp"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-500 text-sm text-center md:text-left">
              © {currentYear} Olney Tech Solutions. Tous droits réservés.
              <br className="md:hidden" />
              <span className="hidden md:inline"> • </span>
              SIRET: 000 000 000 00000
            </div>

            <div className="flex flex-wrap justify-center gap-6">
              {legal.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-gray-500 hover:text-[var(--primary)] transition-colors text-sm font-medium"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.footer>
  );
};

export default Footer;
